import { config, debug } from '../config/environment'
import { storage, STORAGE_KEYS } from '../utils/storage'

// Tipos para a API
export interface Plan {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_interval: string;
  features: string[];
}

export interface CreditCardData {
  card_number: string;
  card_holder_name: string;
  card_expiration_month: number;
  card_expiration_year: number;
  card_cvv: string;
}

export interface SubscriptionRequest {
  plan_type: number;
  payment_method: 'credit_card' | 'pix';
  card_number?: string;
  card_holder_name?: string;
  card_expiration_month?: number;
  card_expiration_year?: number;
  card_cvv?: string;
}

export interface SubscriptionResponse {
  success: boolean;
  message: string;
  subscription_id?: string;
  status?: string;
  plan_name?: string;
  amount?: number;
  payment_url?: string;
  qr_code?: string;
  payment_id?: string;
  qr_code_url?: string;
  pix_key?: string;
  expires_at?: string;
}

export interface SubscriptionStatus {
  success: boolean;
  subscription: {
    user_id: string;
    status: string;
    plan_id: number;
    plan_name: string;
    start_date: string;
    end_date: string;
    next_billing: string;
  };
  message: string;
}

export interface CheckOrderResponse {
  order_id: string;
  order_status: string;
  is_paid: boolean;
  subscription_updated: boolean;
  message: string;
  subscription?: {
    active: boolean;
    expires_at: string;
    created_at: string;
  };
  current_subscription?: {
    active: boolean;
    expires_at: string;
  };
}

// Tipos para autenticação
export interface RegisterData {
  name: string;
  email: string;
  document: string;
  document_type: 'CPF' | 'CNPJ';
  type: 'individual' | 'company';
  phone: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface UserProfile {
  id: string;
  username: string;
  email: string;
  roles: string[];
}


// Tipos para verificação de email
export interface EmailVerificationData {
  email: string;
  code: string;
  token: string;
}

export interface EmailVerificationResponse {
  message: string;
  verified: boolean;
}

export interface VerificationStats {
  email: string;
  attempts: number;
  last_attempt: string;
  verified: boolean;
}

// Classe para gerenciar a API
class ApiService {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.loadAuthToken();
  }

  // Carrega o token do localStorage
  private loadAuthToken(): void {
    this.authToken = storage.getItem<string>(STORAGE_KEYS.AUTH_TOKEN);
  }

  // Atualiza o token
  public setAuthToken(token: string): void {
    this.authToken = token;
    storage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    
    // Salvar timestamp do último login
    storage.setItem(STORAGE_KEYS.LAST_LOGIN, new Date().toISOString());
  }

  // Remove o token
  public clearAuthToken(): void {
    this.authToken = null;
    storage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    storage.removeItem(STORAGE_KEYS.SUBSCRIPTION_CACHE);
    storage.removeItem(STORAGE_KEYS.USER_PROFILE_CACHE);
    storage.removeItem(STORAGE_KEYS.LAST_LOGIN);
  }

  // Obter o token atual
  public getAuthToken(): string | null {
    return this.authToken;
  }

  // Verifica se está autenticado
  public isAuthenticated(): boolean {
    return !!this.authToken;
  }

  // Headers padrão para requisições
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  // Método genérico para fazer requisições
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      if (debug.enabled && debug.showApiCalls) {
        console.log(`🌐 API Request: ${options.method || 'GET'} ${url}`, config);
      }

      const response = await fetch(url, config);
      
      // Se não estiver autenticado, redireciona para login
      if (response.status === 401) {
        this.clearAuthToken();
        throw new Error('Não autenticado. Faça login novamente.');
      }

      const data = await response.json();

      if (debug.enabled && debug.showApiCalls) {
        console.log(`✅ API Response: ${response.status}`, data);
      }

      if (!response.ok) {
        throw new Error(data.detail?.message || data.message || 'Erro na requisição');
      }

      return data;
    } catch (error) {
      if (debug.enabled) {
        console.error(`❌ API Error: ${endpoint}`, error);
      }

      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro de conexão com o servidor');
    }
  }

  // Obter planos disponíveis
  public async getPlans(): Promise<{ success: boolean; plans: Plan[]; message: string }> {
    return this.request<{ success: boolean; plans: Plan[]; message: string }>('/subscriptions/plans');
  }

  // Criar assinatura
  public async createSubscription(data: SubscriptionRequest): Promise<SubscriptionResponse> {
    return this.request<SubscriptionResponse>('/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
}
  // Obter status da assinatura
  public async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    return this.request<SubscriptionStatus>('/subscriptions/status');
  }

  // Verificar status do pedido e atualizar subscription se pago
  public async checkOrderStatus(orderId: string): Promise<CheckOrderResponse> {
    return this.request<CheckOrderResponse>(`/subscriptions/check-order/${orderId}`, {
      method: 'POST',
    });
  }

 
 
  public async validateSubscription(): Promise<any> {
    return this.request<any>('/subscriptions/validate-subscription');
  }

  // Verificar status completo do usuário (autenticação + assinatura)
  public async getUserStatus(): Promise<{
    success: boolean;
    user: UserProfile | null;
    subscription: {
      active: boolean;
      expires_at: string;
      plan_name?: string;
      plan_id?: number;
    } | null;
    message: string;
  }> {
    return this.request<{
      success: boolean;
      user: UserProfile | null;
      subscription: {
        active: boolean;
        expires_at: string;
        plan_name?: string;
        plan_id?: number;
      } | null;
      message: string;
    }>('/user/status');
  }

  // Registro de usuário
  public async register(data: RegisterData): Promise<{ message: string; user_id: string; verification_token?: string }> {
    let url = `${this.baseUrl}/auth/register`;
    url = url.replace('http://', 'https://');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Erro no registro');
    }

    return response.json();
  }

  // Login do usuário
  public async login(data: LoginData): Promise<{ access_token: string; token_type: string }> {
    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Erro no login');
    }

    const result = await response.json();
    this.setAuthToken(result.access_token);
    return result;
  }

  // Obter perfil do usuário
  public async getProfile(): Promise<UserProfile> {
    return this.request<UserProfile>('/auth/profile');
  }

  // Testar autenticação
  public async testAuth(): Promise<any> {
    return this.request<any>('/auth/test-auth');
  }

  // Verificar email
  public async confirmEmailVerification(data: EmailVerificationData): Promise<EmailVerificationResponse> {
    const response = await fetch(`${this.baseUrl}/email-verification/confirm-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Erro na verificação');
    }

    return response.json();
  }

  // Reenviar código de verificação
  public async resendVerificationCode(email: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/email-verification/resend-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Erro ao reenviar código');
    }

    return response.json();
  }

  // Obter estatísticas de verificação
  public async getVerificationStats(email: string): Promise<VerificationStats> {
    const response = await fetch(`${this.baseUrl}/email-verification/verification-stats/${email}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Erro ao obter estatísticas');
    }

    return response.json();
  }

  // Logout
  public logout(): void {
    this.clearAuthToken();
  }

  // Verificar se o token ainda é válido
  public async validateToken(): Promise<boolean> {
    if (!this.authToken) return false;

    try {
      await this.getProfile();
      return true;
    } catch (error) {
      this.clearAuthToken();
      return false;
    }
  }
}

// Instância única do serviço
export const apiService = new ApiService(config.API_URL);

// Hooks personalizados para React
export const useApi = () => {
  return {
    apiService,
    isAuthenticated: apiService.isAuthenticated(),
    register: apiService.register.bind(apiService),
    login: apiService.login.bind(apiService),
    logout: apiService.logout.bind(apiService),
    getProfile: apiService.getProfile.bind(apiService),
    validateToken: apiService.validateToken.bind(apiService),
    confirmEmailVerification: apiService.confirmEmailVerification.bind(apiService),
    resendVerificationCode: apiService.resendVerificationCode.bind(apiService),
    getVerificationStats: apiService.getVerificationStats.bind(apiService),
    checkOrderStatus: apiService.checkOrderStatus.bind(apiService),
    validateSubscription: apiService.validateSubscription.bind(apiService),
    getUserStatus: apiService.getUserStatus.bind(apiService),
  };
};
